import { Component, NgZone, OnDestroy } from '@angular/core';
import { SessionService } from '../../../services/session.service';
import { Router } from '@angular/router';
import { TranslatePipe } from '../../../pipes/translate.pipe';
import { AppComponent } from 'src/app/app.component';
import { PlatformService } from '../../../services/platform.service';
import { ErrorReportingService } from '../../../services/error-reporting.service';
import { TranslationService } from '../../../services/translation.service';
import { MarketService } from '../../../services/market.service';
import { SyncStore } from '../../../services/sync-store.service';
import { AuthService } from '../../../services/auth.service';
import { ClinicService } from '../../../services/clinic.service';
import { consumeBootOAuthError, recordOAuthFailure } from '../../../services/provider-error';
import { homeShownOnDevice } from '../../../shell/home-shown';

@Component({
  selector: 'app-spinner',
  standalone: true,
  imports: [TranslatePipe],
  templateUrl: './spinner.component.html',
  styleUrl: './spinner.component.scss',
})
export class SpinnerComponent implements OnDestroy {
  /** B7: the post-login wait gets the SAME states as every other wait in the
   *  app - an expected window, then a "this is taking longer, nothing is lost"
   *  note, and a real failure with Retry instead of an endless spinner. */
  static readonly DELAYED_AFTER_MS = 12000;
  delayed = false;
  failed = false;
  private delayTimer?: ReturnType<typeof setTimeout>;
  private lastSession: any = null;
  private sessionSub?: { unsubscribe(): void };

  constructor(
    private sessionService: SessionService,
    private router: Router,
    private appComponent: AppComponent,
    private platform: PlatformService,
    private reporter: ErrorReportingService,
    private translation: TranslationService,
    private market: MarketService,
    private syncStore: SyncStore,
    private authService: AuthService,
    private clinics: ClinicService,
    private zone: NgZone,
  ) {
    // Revital brief 29: Apple / Facebook sent her back with an error instead
    // of a session. Waiting here would spin forever; take her back to the
    // screen she started from, where the provider-specific message shows.
    if (consumeBootOAuthError()) {
      const back = recordOAuthFailure();
      this.reporter.logEvent('auth.oauth.return-error', { back: !!back });
      const lang = this.translation.getCurrentLanguage() || 'en';
      void this.router.navigateByUrl(back || `/${lang}/login`, { replaceUrl: true });
      return;
    }
    this.startDelayTimer();
    this.sessionSub = this.sessionService.session$.subscribe(async (session) => {
      if (!session) return;
      this.lastSession = session;
      await this.land(session);
    });
  }

  /** Retry from the failed state: nothing of hers was lost, the session is
   *  already there, so this just runs the landing routine again. */
  retry(): void {
    if (!this.lastSession) return;
    this.failed = false;
    this.delayed = false;
    this.startDelayTimer();
    void this.land(this.lastSession);
  }

  private startDelayTimer(): void {
    if (this.delayTimer) clearTimeout(this.delayTimer);
    this.delayTimer = setTimeout(() => {
      this.delayed = true;
    }, SpinnerComponent.DELAYED_AFTER_MS);
  }

  private async land(session: any): Promise<void> {
    try {
      // Account market is the source of truth for a signed-in user: apply it
      // BEFORE language so the locale resolver serves the India (en-IN) file and
      // the picker/Maccabi gating reflect IN. Refresh the locale if it changed.
      try {
        const changed = this.market.setFromAccount(session.user?.market);
        if (changed) await this.translation.refreshForMarket();
      } catch {}
      // A2: the locale the user was actively using when they started this login
      // wins over the stale account default, so signing in never flips EN<->HE
      // (stashed before the auth attempt, consumed here once). Only when there is
      // no such active choice do we fall back to the account language as the
      // source of truth for a fresh device / web.
      const preferred = this.translation.consumePreferredLocale();
      const accountLang =
        this.market.isIndia() ? 'en' : session.user?.language;
      const desired = preferred || accountLang;
      if (
        (desired === 'en' || desired === 'he' || desired === 'en-GB') &&
        desired !== this.translation.getPickerLanguage()
      ) {
        try {
          await this.translation.changeLanguage(desired);
        } catch {}
      }
      // Pull the account's synced tool data (meals/water/cycle/sleep/widget/…)
      // into localStorage before landing so home paints the right numbers on a
      // new device / reinstall / web.
      try {
        await this.syncStore.hydrate();
      } catch {}
      // A2: route in the language now actually displayed (post preferred/account
      // resolution), so /{lang}/... URLs match the UI and dir stays correct.
      const lang =
        this.translation.getCurrentLanguage() ||
        accountLang ||
        this.appComponent.clerkLocalizationLang ||
        'en';
      // A clinic login / doctor invite was accepted before auth: attach now
      // (consent already given on the branded page) and refresh entitlement.
      let clinicJoined = false;
      try {
        const settled = await this.clinics.settlePending();
        if (settled && settled.kind !== 'failed') {
          clinicJoined = true;
          const u = session.user as any;
          if (settled.response?.isMaccabi) {
            u.isMaccabi = true;
            u.isPaid = true;
            u.isEntitled = true;
          }
          // A clinic can also grant a NUMBER of free tests (credit entries)
          // instead of unlimited access. That never touched isMaccabi, so the
          // session stayed on the free plan and the profile upsold the paywall
          // to a woman who already had free tests. Take the live entitlement
          // the join returns.
          const ent = settled.response?.viewerEntitlement;
          if (ent) {
            if (typeof ent.isEntitled === 'boolean') u.isEntitled = ent.isEntitled;
            if (typeof ent.isPaid === 'boolean') u.isPaid = ent.isPaid;
            if (Array.isArray(ent.creditEntries)) u.creditEntries = ent.creditEntries;
          }
          if (settled.response?.clinic) {
            u.clinics = [...(u.clinics || []).filter((c: any) => c.slug !== settled.response.clinic.slug), settled.response.clinic];
          }
          this.sessionService.saveSession(session);
        }
      } catch {}
      const stored = this.sessionService.consumeRedirectUrl();
      const recovery = sessionStorage.getItem('authIntent') === 'recovery';
      if (recovery) sessionStorage.removeItem('authIntent');
      const userId = session.user?.id;
      let redirectUrl: string;
      // Records WHICH landing branch was taken (no user data, just the path
      // kind) so a "stuck on spinner" report can be traced to a decision.
      let branch: string;

      const signupRedirect = this.authService.getSignupRedirect();
      let latestGuid: string | null = null;

      if (stored) {
        redirectUrl = stored;
        branch = 'stored';
      } else if (clinicJoined && !recovery) {
        // Joined a clinic: straight into the test funnel the doctor sent her to.
        redirectUrl = `/${lang}/app/new-test`;
        branch = 'clinic-join';
      } else if (signupRedirect && !recovery) {
        // Signup finished in a different browser/device than the one that ran
        // the test funnel: the deep link (her results) stored at signup travels
        // via Supabase user metadata. One-time — cleared after landing so later
        // logins take the normal route.
        redirectUrl = signupRedirect;
        this.authService.clearSignupRedirect();
        branch = 'signup-redirect';
      } else if (recovery) {
        // Reset-password landing → the woman's report, or new-test if she has
        // no completed scan yet.
        const guid = userId
          ? await this.sessionService.getLatestCompletedScanGuid(userId)
          : null;
        redirectUrl = guid
          ? `/${lang}/app/doctor-report/${guid}`
          : `/${lang}/app/new-test`;
        branch = 'recovery';
      } else if (
        this.platform.isNative &&
        !homeShownOnDevice() &&
        (latestGuid = userId
          ? await this.sessionService.getLatestCompletedScanGuid(userId)
          : null)
      ) {
        // Surface split A1 (Danna 25.09): the first sign-in on THIS device,
        // with a finished mapping, opens her results, not an empty home. The
        // device flag is set the first time home renders; from then on the
        // native branch below applies as before. No guid (none, or the call
        // failed) falls through to it.
        redirectUrl = `/${lang}/app/results/${latestGuid}`;
        branch = 'native-first-results';
      } else if (this.platform.isNative) {
        // Native lands straight in the app shell HOME (spec §3: no app
        // onboarding).
        redirectUrl = `/${lang}/app/home`;
        branch = 'native-home';
      } else {
        // Surface split A2 (Danna 25.09): the web has no daily loop any more,
        // so a woman with a finished mapping lands on her latest results.
        const guid = userId
          ? await this.sessionService.getLatestCompletedScanGuid(userId)
          : null;
        redirectUrl = guid
          ? `/${lang}/app/results/${guid}`
          : `/${lang}/app/new-test`;
        branch = guid ? 'web-results' : 'web-new-test';
      }

      this.reporter.logEvent('auth.land', { branch });
      console.log('SpinnerComponent: redirecting to', redirectUrl);
      if (this.delayTimer) clearTimeout(this.delayTimer);
      // The session arrives from the auth SDK outside Angular's zone; route
      // inside it so the shell (nav) re-renders for the landing page.
      await this.zone.run(() => this.router.navigate([redirectUrl]));
    } catch (e) {
      // Whatever broke, she must not be left staring at a spinner for ever.
      console.error('SpinnerComponent: landing failed:', e);
      this.reporter.report(e, 'auth');
      if (this.delayTimer) clearTimeout(this.delayTimer);
      this.failed = true;
    }
  }

  ngOnDestroy() {
    if (this.delayTimer) clearTimeout(this.delayTimer);
    // Drop only OUR subscription. Unsubscribing the shared subject itself
    // closed it for the whole app, so anything subscribing after the landing
    // (e.g. the nav) threw and never rendered.
    this.sessionSub?.unsubscribe();
  }
}
